import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {DateInputDirective} from "../../../shared/directives/date-input.directive";
import {MatButton} from "@angular/material/button";
import {
  MatDatepickerToggle,
  MatDatepickerToggleIcon,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate
} from "@angular/material/datepicker";
import {MatInput, MatSuffix} from "@angular/material/input";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {RecordsTableModule} from "../records-table/records-table.module";
import {FiltersValues, RecordDataValue, RecordTableColumnParams} from "../../types/records-table.types";
import {ResourceParams} from "../../models/resource/resources";
import {defaultResourceParams} from "../../models/resource/default-resource-params";
import {FiltersVisibilityService} from "../../services/filters-visibility.service";
import {RecordsTableService} from "../../services/records-table.service";
import {UtilityService} from "../../services/utility.service";
import {Subscription} from "rxjs";
import {MatIcon} from "@angular/material/icon";
import {RecordsTableFiltersService} from "./services/records-table-filters.service";
import {filterOnlyFilterableParams} from "./configs/records-table-filters-configs";
import {smoothShowHideHeight} from "../../../shared/animations/smoothShowHideAnimations";
import {UrlQueryParamsStateService} from "../../services/table-services/url-query-params-state.service";
import {TableContextService} from "../../services/table-services/table-context.service";
import {customDateProvider} from "../../locale/providers/custom-date.provider";
import {MatTooltip} from "@angular/material/tooltip";

@Component({
  selector: 'app-records-table-filters',
  imports: [
    DateInputDirective,
    MatButton,
    MatDateRangeInput,
    MatDateRangePicker,
    MatDatepickerToggle,
    MatDatepickerToggleIcon,
    MatEndDate,
    MatIcon,
    MatInput,
    MatStartDate,
    MatSuffix,
    ReactiveFormsModule,
    RecordsTableModule,
    MatTooltip
  ],
  templateUrl: './records-table-filters.component.html',
  styleUrl: './records-table-filters.component.scss',
  providers: [
    ...customDateProvider,
    TableContextService
  ],
  animations: [ smoothShowHideHeight ]
})
export class RecordsTableFiltersComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true, transform: filterOnlyFilterableParams }) filtersParams: Array<RecordTableColumnParams> = [];
  @Input({ required: true }) resourceParams: ResourceParams = defaultResourceParams;
  @Output() filtersChanged: EventEmitter<FiltersValues> = new EventEmitter<FiltersValues>();
  @Output() toggleFiltersVisible: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('filtersTable') filtersTableRef!: ElementRef<HTMLDivElement>;

  public filtersVisibilityService: FiltersVisibilityService = inject(FiltersVisibilityService);
  private recordsTableService: RecordsTableService = inject(RecordsTableService);
  private utilityService: UtilityService = inject(UtilityService);
  private recordsTableFiltersService: RecordsTableFiltersService = inject(RecordsTableFiltersService);
  private urlQueryParamsStateService: UrlQueryParamsStateService = inject(UrlQueryParamsStateService);

  public dateRanges: { [key: string]: FormGroup } = {};
  public filtersValues!: FiltersValues;
  private subscriptions: Subscription = new Subscription();

  public ngOnInit(): void {
    this.initResourceTypeChangeSubscription();
    this.checkFiltersAndLoadData();
  }

  public ngOnChanges(): void {
    this.initFiltersValues();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public handleFiltersValuesChange(key: string, value: RecordDataValue): void {
    // @ts-ignore
    value = value?.hasOwnProperty('id') ? value.id : value;
    this.filtersValues[key] = value || null;

    this.filtersChanged.emit(this.filtersValues);
  }

  public dateRangeChange(startValue: string, endValue: string, filterParam: RecordTableColumnParams): void {
    this.filtersValues[`${filterParam.key}From`] = startValue;
    this.filtersValues[`${filterParam.key}To`] = endValue;
    this.filtersChanged.emit(this.filtersValues);
  }

  public clearDateRange(filterParam: RecordTableColumnParams): void {
    this.dateRanges[filterParam.key].reset();
    this.filtersValues[`${filterParam.key}From`] = null;
    this.filtersValues[`${filterParam.key}To`] = null;
    this.filtersChanged.emit(this.filtersValues);
  }

  public handleToggleFiltersVisible(): void {
    this.filtersVisibilityService.toggleFiltersVisibility(0);
  }

  public onFilterAnimationDone(): void {
    const realHeight = this.filtersTableRef?.nativeElement.offsetHeight || 0;
    this.filtersVisibilityService.updateFilterHeight(realHeight);
    this.toggleFiltersVisible.emit();
  }

  public isFiltersEmpty(): boolean {
    const activeFilters: FiltersValues = this.utilityService.removeEmptyProperties(this.filtersValues)
    return this.utilityService.isObjectEmpty(activeFilters);
  }

  public getFormToNumberKey(key: string, rangePart: 'From' | 'To'): string {
    return `${key}${rangePart}`;
  }

  public handleResetFilter(): void {
    this.filtersValues = {};
    this.resetDateRange();
    this.filtersChanged.emit({});
  }

  private clearFilters(): void {
    this.filtersValues = this.recordsTableFiltersService.createFiltersValues(this.filtersParams);
    this.resetDateRange();
  }

  private initResourceTypeChangeSubscription(): void {
    this.subscriptions.add(
      this.recordsTableService.currentResourceType$.subscribe({
        next: () => this.initFiltersValues()
      })
    )
  }

  private setDateRange(): void {
    this.filtersParams.forEach(filterParam => {
      if (filterParam.type === 'DATE') {
        this.dateRanges[filterParam.key] = new FormGroup({
          fromPlannedDate: new FormControl(this.utilityService.convertToDate(this.filtersValues?.[`${filterParam.key}From`])),
          toPlannedDate: new FormControl(this.utilityService.convertToDate(this.filtersValues?.[`${filterParam.key}To`])),
        });
      }
    });
  }

  private checkFiltersAndLoadData(): void {
    const hasFilters = Object.values(this.filtersValues).some(value => value);
    hasFilters && this.filtersChanged.emit(this.filtersValues);
  }

  private resetDateRange(): void {
    for (let key in this.dateRanges) {
      this.dateRanges.hasOwnProperty(key) && this.dateRanges[key].reset();
    }
  }

  private initFiltersValues(): void {
    this.clearFilters()
    this.filtersValues = this.recordsTableFiltersService.createFiltersValues(this.filtersParams);
    const hasValue = this.urlQueryParamsStateService.hasUrlQueryParamsState();
    const filtersFromPreferences = this.recordsTableFiltersService.getFiltersFromUserPreferences();
    if (filtersFromPreferences && !hasValue) {
      this.filtersValues = filtersFromPreferences
    }

    this.setDateRange();
  }
}
